import { Link } from 'react-router-dom';

interface UrgentProjectSpotlightProps {
  project: {
    id: number;
    title: string;
    description?: string;
    imageUrl?: string;
    goalAmount?: number;
    raisedAmount?: number;
    progressPercentage?: number;
    location?: string;
    priority?: string;
    isFeatured?: boolean;
  };
  
  compact?: boolean;
}

export default function UrgentProjectSpotlight({ project, compact = false }: UrgentProjectSpotlightProps) {
  return (
    <Link
      to={`/projects/${project.id}`}
      className={`group relative block overflow-hidden rounded-2xl border-2 border-red-500/40 shadow-lg hover:shadow-xl hover:border-red-500/70 transition-all ${
        compact ? 'min-h-[140px]' : 'min-h-[220px] md:min-h-[260px]'
      }`}
    >
      <div className="absolute inset-0">
        {project.imageUrl ? (
          <img
            src={project.imageUrl}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full" style={{ background: 'linear-gradient(135deg,#7f1d1d,#b91c1c,#0e7490)' }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/40" />
      </div>

      <div className={`relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-4 ${compact ? 'p-5' : 'p-6 md:p-8'}`}>
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-white" />
              Urgent Appeal
            </span>
            {project.location && (
              <span className="bg-white/15 text-white/90 text-[10px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm">
                {project.location}
              </span>
            )}
            {project.isFeatured && (
              <span className="bg-amber-400 text-amber-950 text-[10px] font-bold px-2.5 py-1 rounded-full">
                ★ Featured
              </span>
            )}
          </div>

          <h2 className={`font-bold text-white font-display leading-tight mb-2 ${compact ? 'text-xl' : 'text-2xl md:text-3xl'}`}>
            {project.title}
          </h2>
          {!compact && project.description && (
            <p className="text-white/75 text-sm leading-relaxed line-clamp-2 mb-4 max-w-xl">
              {project.description}
            </p>
          )}

        </div>

        <div className="shrink-0 flex flex-col sm:flex-row gap-2">
          <span className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold text-sm px-6 py-3 rounded-xl shadow-lg group-hover:scale-[1.03] transition-transform">
            Donate now
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}
